import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Users } from "lucide-react";
import Intake from "@/components/prevent/Intake";
import Timeline from "@/components/prevent/Timeline";
import RiskReader from "@/components/prevent/RiskReader";
import Moves from "@/components/prevent/Moves";
import Family from "@/components/prevent/Family";
import PreventionHero from "@/components/prevent/PreventionHero";
import useProgress from "@/lib/useProgress";
import { PLATFORMS } from "@/lib/platforms";
import { ASKED } from "@/lib/preventionData";

const PAPER = "#F2F7FB";
const INK = "#122534";
const SOFT = "rgba(18,37,52,0.6)";
const BLUE = "#4C9EDA";
const DEEP = "#2E6FA3";

/* the years, receding — the entrance visual */
const YEARS = [0, 1, 2, 3, 4, 5, 6, 7];

function Asked() {
  const [on, setOn] = useState(0);
  return (
    <section style={{ background: PAPER }} data-testid="prevention-asked">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
        <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(18,37,52,0.4)" }}>The honest part</p>
        <h2 className="font-display text-[30px] sm:text-[40px] leading-[1.04] mt-2 mb-8" style={{ color: INK }}>Three things most prevention writing leaves out</h2>
        <div className="space-y-2">
          {ASKED.map((e, i) => (
            <div key={e.q} className="rounded-2xl overflow-hidden" style={{ background: on === i ? "#fff" : "rgba(255,255,255,0.65)", border: "1px solid rgba(18,37,52,0.07)" }}>
              <button onClick={() => setOn(on === i ? -1 : i)} className="w-full flex items-center gap-4 px-6 py-4 text-left" data-testid={`prevention-asked-${i}`}>
                <span className="font-display text-[18px] flex-1" style={{ color: INK }}>{e.q}</span>
                <ChevronDown size={15} style={{ color: "rgba(18,37,52,0.35)", transform: on === i ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
              </button>
              <AnimatePresence initial={false}>
                {on === i && (
                  <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden px-6 pb-5 text-[15px] leading-[1.85]" style={{ color: SOFT }}>{e.a}</motion.p>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Prevention() {
  const [data, patch] = useProgress("prevention", { age: null, sex: null, flags: [], done: {}, family: [] });
  const [phase, setPhase] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const has = !!data.age && !!data.sex;
  const stage = phase || "welcome";
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [stage]);

  return (
    <main className="pt-16" style={{ background: PAPER }} data-testid="platform-page-prevention">
      <section ref={heroRef} className="relative overflow-hidden" style={{ minHeight: stage === "timeline" ? undefined : "calc(100svh - 4rem)" }} data-testid="prevention-stage">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #E4F0F9 0%, #EDF4FA 52%, #F2F7FB 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(44% 40% at 88% 6%, rgba(76,158,218,0.3), transparent 72%), radial-gradient(38% 40% at 0% 90%, rgba(46,111,163,0.18), transparent 72%)" }} />
        {/* receding years */}
        <div className="absolute inset-x-0 bottom-0 h-[52%] pointer-events-none" aria-hidden>
          {YEARS.map((n) => (
            <motion.span key={n} className="absolute left-0 right-0" style={{ bottom: `${n * 12}%`, height: 1, background: `rgba(46,111,163,${0.16 - n * 0.017})` }}
              initial={{ scaleX: 0.7, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }} transition={{ delay: 0.3 + n * 0.09, duration: 0.9, ease: [0.22, 1, 0.36, 1] }} />
          ))}
          {YEARS.map((n) => (
            <motion.span key={`d${n}`} className="absolute rounded-full" style={{ bottom: `${n * 12}%`, left: `${8 + n * 11}%`, width: 7 - n * 0.5, height: 7 - n * 0.5, background: DEEP, opacity: 0.5 - n * 0.05, marginBottom: -3 }}
              animate={{ y: [0, -5, 0] }} transition={{ duration: 5 + n, repeat: Infinity, ease: "easeInOut" }} />
          ))}
        </div>

        <motion.div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 flex flex-col justify-center" style={{ minHeight: stage === "timeline" ? undefined : "calc(100svh - 4rem)", y: stage === "timeline" ? 0 : contentY }}>
          <AnimatePresence mode="wait">
            {stage === "welcome" && (
              <motion.div key="welcome" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -20, filter: "blur(8px)" }} transition={{ duration: 0.55 }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center w-full">
                <div className="lg:col-span-6">
                  <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "rgba(18,37,52,0.45)" }}>Prevention and screening · for people who are not ill</motion.p>
                  <h1 className="font-display text-[38px] sm:text-[54px] lg:text-[64px] leading-[0.99] mt-5" style={{ color: INK }}>
                    {[["The", 0], ["next", 1], ["thirty", 2], ["years,", 3], ["\n", -1], ["written", 4], ["down", 5], ["\n", -1], ["in", 6], ["order.", 7]].map(([w, i], n) =>
                      w === "\n" ? <br key={n} /> : (
                        <motion.span key={n} initial={{ opacity: 0, y: 22, filter: "blur(7px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                          transition={{ delay: 0.16 + i * 0.085, duration: 0.72, ease: [0.22, 1, 0.36, 1] }} className="inline-block mr-[0.24em]" style={{ color: i >= 4 ? DEEP : INK }}>{w}</motion.span>
                      ))}
                  </h1>
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }} className="text-[16px] sm:text-[18px] mt-6 max-w-md leading-relaxed" style={{ color: SOFT }}>
                    Three answers, and you get a calendar that runs decades forward. Every item carries the case against it as well as the case for.
                  </motion.p>
                  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} className="mt-8 flex flex-wrap items-center gap-4">
                    <button onClick={() => setPhase(has ? "timeline" : "intake")} className="group inline-flex items-center gap-3 rounded-full px-8 py-4 text-base font-semibold text-white transition-transform hover:-translate-y-0.5"
                      style={{ background: DEEP, boxShadow: "0 24px 50px -20px rgba(46,111,163,0.75)" }} data-testid="prevention-begin">
                      {has ? "Back to my timeline" : "Build my timeline"}
                      <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    <p className="text-[13px]" style={{ color: "rgba(18,37,52,0.45)" }}>Ninety seconds. No account,<br className="hidden sm:block" /> nothing stored off your device.</p>
                  </motion.div>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="mt-9 flex flex-wrap gap-x-7 gap-y-3">
                    {[["18", "screenings, checks and vaccines"], ["2 sides", "for every single one"], ["0", "scare tactics"]].map(([a, b]) => (
                      <div key={b}>
                        <p className="font-display text-2xl leading-none" style={{ color: DEEP }}>{a}</p>
                        <p className="text-[11px] mt-1" style={{ color: "rgba(18,37,52,0.45)" }}>{b}</p>
                      </div>
                    ))}
                  </motion.div>
                </div>

                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="lg:col-span-6" data-testid="prevention-hero-animation">
                  <PreventionHero />
                </motion.div>
              </motion.div>
            )}
            {stage === "intake" && <Intake key="intake" onDone={(a) => { patch({ ...a, done: {} }); setPhase("timeline"); }} onLeave={() => setPhase("welcome")} />}
            {stage === "timeline" && has && (
              <motion.div key="timeline" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }} className="w-full">
                <Timeline data={data} patch={patch} onEntrance={() => setPhase("welcome")} onRedo={() => { patch({ age: null, sex: null, flags: [], done: {} }); setPhase("intake"); }} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {stage !== "timeline" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute left-0 right-0 bottom-5 flex justify-center pointer-events-none">
            <p className="text-[10px] tracking-[0.2em] uppercase flex items-center gap-2" style={{ color: "rgba(18,37,52,0.32)" }}>the instruments are below <ChevronDown size={12} className="floaty" /></p>
          </motion.div>
        )}
      </section>

      <RiskReader />
      <Moves />
      <Family picked={data.family} onToggle={(k) => patch((d) => ({ family: (d.family || []).includes(k) ? d.family.filter((x) => x !== k) : [...(d.family || []), k] }))} />

      <section style={{ background: "linear-gradient(180deg, #F2F7FB, #E8F1F8)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
          <div className="rounded-[2rem] p-8 sm:p-10 text-white relative overflow-hidden grain" style={{ background: `linear-gradient(140deg, ${BLUE}, #1F5680)` }} data-testid="prevention-room">
            <span className="absolute -right-16 -top-16 w-56 h-56 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.3), transparent 70%)" }} />
            <div className="flex items-start gap-4 relative">
              <Users size={22} className="mt-1 shrink-0" />
              <div>
                <h2 className="font-display text-2xl sm:text-3xl">One room, mostly questions that start with should I</h2>
                <p className="mt-2.5 max-w-xl text-[15px] opacity-90">Should I take the PSA test. Should I bother with the flu jab. Should I be worried about this headline. Answered by clinicians under their real names.</p>
                <p className="mt-4 text-[12px] opacity-75">Opens with the full platform. Your alias already works here.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Asked />

      {stage !== "timeline" && (
        <section style={{ background: PAPER }}>
          <div className="max-w-5xl mx-auto px-5 sm:px-8 pb-16">
            <div className="rounded-[2rem] px-7 py-9 flex flex-col sm:flex-row sm:items-center gap-5 relative overflow-hidden text-white" style={{ background: `linear-gradient(140deg, ${DEEP}, #143850)` }}>
              <span className="absolute -right-16 -top-16 w-52 h-52 rounded-full halo" style={{ background: "radial-gradient(circle, rgba(76,158,218,0.4), transparent 70%)" }} />
              <p className="font-display text-xl sm:text-[26px] leading-snug flex-1 relative">Three answers, and the next thirty years arrive in order.</p>
              <button onClick={() => { setPhase("intake"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="shrink-0 relative rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: "#fff", color: INK }} data-testid="prevention-bottom-cta">
                Build my timeline
              </button>
            </div>
          </div>
        </section>
      )}

      <section style={{ background: "#E7EFF6", borderTop: "1px solid rgba(18,37,52,0.07)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <p className="kicker mb-4" style={{ color: "rgba(18,37,52,0.4)" }}>The other doors</p>
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.filter((x) => x.key !== "prevention").map((x) => (
              <Link key={x.key} to={x.path} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5"
                style={{ background: "#fff", border: "1px solid rgba(18,37,52,0.08)", color: INK }} data-testid={`door-${x.key}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: x.hue }} />{x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
